import { z } from "zod";
import { SystemEntity } from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";

/**
 * Map 845 to SPA
 *
 * Adapter between two existing steps: takes one contract out of a parsed X12
 * 845 and emits the record shape `persist_spa_from_extraction` accepts.
 *
 * ```
 * Parse EDI → Split 845 Contracts → Map 845 to SPA → Upsert SPA
 * ```
 *
 * **One contract, one SPA.** The input is a single contract's view of the
 * document rather than the whole parse, because a `CON` loop is what a SPA
 * corresponds to and a document may carry up to 37 of them. `split_845_contracts`
 * produces those views as an array; wiring it into this step's single-value
 * `contract` input fans this step and Upsert SPA out to one run per contract.
 *
 * **Supplier-agnostic, and deliberately unconfigurable.** An 845 is a standard:
 * the contract number is in `CON02`, the end customer in `N1|EN`, the product
 * in `LIN03`, a price in `CTP03` or a multiplier in `CTP07`, the window in
 * `DTM 092`/`093` — for every sender. Suppliers differ in which party role
 * carries a branch and which element carries a class of trade, and this step
 * sidesteps that by recording rather than interpreting: every party keeps its
 * raw `N101` role code and every price component is passed through verbatim.
 *
 * Adding a supplier therefore needs no change here and no new configuration.
 *
 * The `vendor` value `persist_spa_from_extraction` requires is resolved here,
 * from the email subject, because it appears nowhere else: one VAN relay
 * forwards every supplier, so the sender is identical for all of them, and an
 * 845 names the parties to the agreement rather than who sent it. Wire the
 * trigger's `subject` in and this step's `vendor` out; a subject naming no
 * supplier we ingest fails the run rather than defaulting.
 */
export const map845ToSpaStep = defineStepConfig({
  type: "map_845_to_spa",
  name: "Map 845 to SPA",
  description:
    "Convert one contract out of a parsed X12 845 into the SPA record and line items that Upsert SPA accepts, taking the supplier from the `vendor` input when one is wired and reading it from the email subject otherwise. There is no per-vendor configuration on this step: where a supplier's codes mean something of their own, that is stated on the interpretation profile of the EDI guideline they sent us, which this step resolves for the supplier it read from the subject. A supplier with no active guideline is read exactly as the bare standard.",
  category: "Transformation",
  libraryModule: "Pricing",

  inputSchema: z.object({
    contract: z
      .record(z.string(), z.unknown())
      .describe(
        "One contract's view of the parsed document, as { header, lineItems }. Wire from Split 845 Contracts' contracts output — an array there fans this step out to one SPA per contract.",
      ),
    subject: z
      .string()
      .optional()
      .describe(
        "The inbound email's subject line. Wire from the trigger's subject — it is the only place the supplier is named. Omit only when the supplier is supplied directly to Upsert SPA instead, and then wire `vendor` here so the sender's guideline is still resolved.",
      ),
    vendor: z
      .string()
      .optional()
      .describe(
        "The supplier key, when the caller already knows it and there is no subject to read it from — e.g. 'schneider'. Takes precedence over `subject`. Without it, a run that omits the subject is read against no guideline at all, and records that it was: the guideline a supplier has in force cannot be found without knowing which supplier sent the document.",
      ),
  }),

  outputSchema: z.object({
    record: z
      .record(z.string(), z.unknown())
      .describe(
        "SPA header fields (spaNumber, quoteType, startDate, endDate, currency, customerNumber, customerName, contractStatus, purposeCode). Wire to Upsert SPA's record.",
      ),
    lineItemsJsonDocument: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "SPA line items, as a JSON document rather than inline. productNumber, keptFlag and dropReason become columns; every other key is kept in the line's rawData. Wire to Upsert SPA's lineItemsJsonDocument — named for the encoding because Upsert SPA's `lineItemsDocument` means the older CSV, and a same-name wiring would type-check and then fail inside the CSV reader. They leave the payload because a large authorization's lines exceed the workflow result limit, which failed the run after parsing and splitting had both succeeded — and did so only for the largest agreements.",
    ),
    parties: z
      .array(z.record(z.string(), z.unknown()))
      .describe(
        "Every party the document names, verbatim and in document order — one entry per N1 segment, with its raw N101 role code and its position. Never collapsed or deduplicated on role plus code: a supplier restates a party as often as its document structure requires, and Rockwell repeats the whole branch list under every End Customer, so the repeat is what records which branches belong to which customer. A consumer wanting the distinct set derives it (see distinctPartyCodes) rather than expecting one entry per party. Which of them is a branch differs by supplier, so that judgement is left to the consumer too. Wire to Upsert SPA's parties.",
      ),
    vendor: z
      .string()
      .describe(
        "Supplier key — the `vendor` input when one was given, otherwise read from the subject, e.g. 'rockwell'. Wire to Upsert SPA's vendor. Empty when neither was supplied.",
      ),
    ediGuidelineId: z
      .string()
      .nullable()
      .describe(
        "The vendor EDI guideline this contract was read against, or null when the sending partner has none in force. Null means the standard reading applied and nothing authoritative was consulted — which is what `read_without_guideline` in interpretationFlags states in words.",
      ),
    interpretationFlags: z
      .array(z.string())
      .describe(
        "What the sending partner's guideline could not answer: `read_without_guideline` (no active guideline for this partner), `undefined_transfer_type` (a PAD02 code their own guideline does not define, so the record carries no quote type rather than being labelled ship-and-debit), `mixed_transfer_types` (the contract's lines resolve to more than one agreement type), `undefined_contract_status` (a CON03 code their guideline does not document, still stored verbatim). Empty when the guideline answered everything the document asked of it.",
      ),
    partyCount: z.number().int().describe("parties.length"),
    pricingFlag: z
      .string()
      .nullable()
      .describe(
        "Why the authorization states no usable pricing — `no_price_or_factor` for a contract stating neither a net price nor a multiplier on any line, which 20% of Rockwell documents do — or null when it prices something. Flagged records are still mapped in full and must not be read as priced agreements.",
      ),
    contractStatus: z
      .string()
      .nullable()
      .describe(
        "CON03 — the status the vendor stated for this contract, verbatim: OC original, CM modified, EX expired. An EX contract is a termination rather than an agreement, and usually carries no lines at all; it must not be read as a priced SPA on the strength of its lines being empty. Recorded as sent, so a code no guideline documents is passed through rather than defaulted. Null when the contract states none.",
      ),
    purposeCode: z
      .string()
      .nullable()
      .describe(
        "BPA01 — what the vendor said this document is, verbatim: 00 an original authorization, 04 a change. Per document rather than per contract, so every contract split out of one document reports the same code. It is what distinguishes an unpriced 845 that opens a contract, with pricing following separately under the same number, from one authorizing no discount — pricingFlag marks both identically. Recorded as sent, never reduced to original-or-not. Null when the document states none.",
      ),
    lineItemCount: z.number().int().describe("lineItems.length"),
    keptLineItemCount: z
      .number()
      .int()
      .describe(
        "Lines with usable pricing. The difference from lineItemCount is lines the supplier sent that state no price and no window; those are kept and flagged, never dropped.",
      ),
  }),

  configSchema: z.object({}),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "2 minutes",
  },
});
